/**
 * @graphql agg
 */

import gql from 'graphql-tag';

import {
	useInTrialFeatureGatesExperimentEnrollmentAB as baseUseInTrialFeatureGatesExperimentEnrollmentAB,
	type UseInTrialFeatureGatesExperimentEnrollmentABProps,
} from '@atlassian/in-trial-feature-gates-banner';

import { expValEquals } from '@confluence/feature-experiments';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useInTrialFeatureGatesExperimentEnrollmentABQuery as useInTrialFeatureGatesExperimentEnrollmentABQueryType,
	useInTrialFeatureGatesExperimentEnrollmentABQueryVariables,
} from './__types__/useInTrialFeatureGatesExperimentEnrollmentABQuery.ts';

const UseInTrialFeatureGatesExperimentEnrollmentABQuery = gql`
	query useInTrialFeatureGatesExperimentEnrollmentABQuery($cloudId: ID!) {
		tenantContexts(cloudIds: [$cloudId])
			@optIn(to: ["CcpAllUserUpgradeAndPay", "HamsAllUserUpgradeAndPay"]) {
			entitlementInfo(hamsProductKey: "confluence.ondemand") {
				entitlement(where: { AND: [{ inTrialOrPreDunning: true }] }) {
					subscription {
						trial {
							endTimestamp
						}
						accountDetails {
							invoiceGroup {
								invoiceable
								experienceCapabilities {
									configurePaymentV2 {
										experienceUrl
										isAvailableToUser
									}
									configurePayment {
										experienceUrl
										isAvailableToUser
									}
								}
							}
						}
					}
				}
			}
		}
	}
`;

export const useInTrialFeatureGatesExperimentEnrollmentAB = (
	props: Omit<
		UseInTrialFeatureGatesExperimentEnrollmentABProps,
		'aggClient' | 'inTrialFeatureGatesQueryResult'
	>,
): ReturnType<typeof baseUseInTrialFeatureGatesExperimentEnrollmentAB> => {
	const aggClient = getAGGClient();

	const queryResult = useQueryInterop<
		useInTrialFeatureGatesExperimentEnrollmentABQueryType,
		useInTrialFeatureGatesExperimentEnrollmentABQueryVariables
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
	>(UseInTrialFeatureGatesExperimentEnrollmentABQuery, {
		client: aggClient,
		variables: { cloudId: props.tenantId },
		skip: !expValEquals('confluence_relay_migration_no_client_passing', 'isEnabled', true),
	});

	const baseProps: UseInTrialFeatureGatesExperimentEnrollmentABProps = expValEquals(
		'confluence_relay_migration_no_client_passing',
		'isEnabled',
		true,
	)
		? {
				inTrialFeatureGatesQueryResult: queryResult,
				...props,
			}
		: {
				aggClient,
				...props,
			};

	return baseUseInTrialFeatureGatesExperimentEnrollmentAB(baseProps);
};
